/**
 * Unified app-resume bus + latency instrumentation.
 *
 * WKWebView (Capacitor) does not reliably fire `visibilitychange` when the app
 * is restored from the iOS app switcher: sometimes only `focus` fires, sometimes
 * only Capacitor's `appStateChange`. Every consumer used to subscribe to its own
 * subset of events, so some ran twice (heavy Supabase refetches back to back,
 * blocking the main thread for ~2s) and some never ran at all.
 *
 * This module centralises the detection, coalesces duplicates inside a short
 * window, measures how long it takes until the first frame is painted, and lets
 * consumers defer their heavy work until AFTER interactivity is restored.
 */

export type ResumeSource = "visibilitychange" | "pageshow" | "focus" | "appStateChange";

export interface ResumeDetail {
  source: ResumeSource;
  hiddenMs: number;
  at: number;
}

export const APP_RESUME_EVENT = "mblearn:resume";

const COALESCE_MS = 700;

let started = false;
let lastResumeAt = 0;
let hiddenSince = 0;

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Run a callback once the browser is idle, never blocking the resume frame. */
export function runWhenIdle(callback: () => void, timeoutMs = 1200): () => void {
  if (typeof window === "undefined") {
    callback();
    return () => undefined;
  }
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: IdleRequestCallback, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  }).requestIdleCallback;

  if (typeof ric === "function") {
    const handle = ric(() => callback(), { timeout: timeoutMs });
    return () => {
      try {
        (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(handle);
      } catch {
        /* noop */
      }
    };
  }

  const timer = window.setTimeout(callback, Math.min(timeoutMs, 300));
  return () => window.clearTimeout(timer);
}

function emitResume(source: ResumeSource) {
  if (typeof document === "undefined") return;
  if (document.visibilityState === "hidden") return;

  const at = Date.now();
  if (at - lastResumeAt < COALESCE_MS) return;
  lastResumeAt = at;

  const hiddenMs = hiddenSince ? at - hiddenSince : 0;
  hiddenSince = 0;

  const t0 = nowMs();
  const detail: ResumeDetail = { source, hiddenMs, at };
  window.dispatchEvent(new CustomEvent<ResumeDetail>(APP_RESUME_EVENT, { detail }));

  // Time-to-first-frame after resume: rAF only fires once the WebView is
  // actually rendering again, so this is the honest "app is visible" metric.
  requestAnimationFrame(() => {
    const firstFrameMs = Math.round(nowMs() - t0);
    requestAnimationFrame(() => {
      const secondFrameMs = Math.round(nowMs() - t0);
      try {
        console.info("⏱️ [Resume]", {
          source,
          hiddenMs,
          firstFrameMs,
          interactiveFrameMs: secondFrameMs,
          route: window.location.pathname,
        });
      } catch {
        /* noop */
      }
    });
  });
}

export function onAppResume(handler: (detail: ResumeDetail) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const listener = (event: Event) => handler((event as CustomEvent<ResumeDetail>).detail);
  window.addEventListener(APP_RESUME_EVENT, listener);
  return () => window.removeEventListener(APP_RESUME_EVENT, listener);
}

export function startAppResumeBus(): () => void {
  if (typeof window === "undefined" || started) return () => undefined;
  started = true;

  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      hiddenSince = Date.now();
      return;
    }
    emitResume("visibilitychange");
  };
  const onFocus = () => emitResume("focus");
  const onPageShow = () => emitResume("pageshow");

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("pageshow", onPageShow);

  // Capacitor native app-state (the only reliable signal on iOS app switcher).
  // Read through the injected bridge global so no extra npm dependency and no
  // build/native change is required.
  let removeNative: (() => void) | undefined;
  try {
    const cap = (window as unknown as {
      Capacitor?: {
        isNativePlatform?: () => boolean;
        Plugins?: {
          App?: {
            addListener?: (
              event: string,
              cb: (state: { isActive: boolean }) => void,
            ) => { remove?: () => void } | Promise<{ remove?: () => void }>;
          };
        };
      };
    }).Capacitor;

    if (cap?.isNativePlatform?.() && cap.Plugins?.App?.addListener) {
      const handle = cap.Plugins.App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) {
          hiddenSince = Date.now();
          return;
        }
        emitResume("appStateChange");
      });
      removeNative = () => {
        void Promise.resolve(handle).then((h) => h?.remove?.());
      };
    }
  } catch {
    /* bridge unavailable on web — the DOM events above cover it */
  }


  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("pageshow", onPageShow);
    removeNative?.();
    started = false;
  };
}
